import { wrapTextWithBreaks } from './calculateFontSize';

/**
 * Плашка заголовка в посте: раньше высота была зашита числом 270, текст
 * центрировался в ней, и лишние строки уходили за нижний край картинки.
 *
 * Новое поведение (согласовано с медиа): кегль постоянный, а плашка растёт
 * под текст. Чтобы длинный заголовок не закрыл всё фото, у неё есть потолок;
 * упёрлись в потолок — ужимаем кегль, но не ниже пола. Если и на полу текст
 * не помещается, плашка всё-таки перерастает потолок: обрезать текст нельзя.
 */
export const BAND_DEFAULTS = {
  /** Плашка не ниже этого (историческая высота) — короткие заголовки выглядят как раньше. */
  minHeight: 270,
  /** Потолок высоты — доля высоты холста. */
  maxFraction: 0.4,
  /** Кегль не ниже этой доли от заданного. */
  minFontScale: 0.75,
  /** Какая доля высоты плашки отдана тексту; остальное — внутренние отступы. */
  textFraction: 0.8,
} as const;

export interface BandLayoutInput {
  text: string;
  /** Кегль в render-space px (уже умноженный на scale). */
  fontSize: number;
  /** Множитель межстрочного расстояния. */
  lineSpacing: number;
  /** Ширина переноса в render-space px. */
  maxWidth: number;
  /** Высота холста в render-space px — от неё считается потолок. */
  canvasHeight: number;
  fontFamily: string;
  ctx: CanvasRenderingContext2D;
  minHeight?: number;
  maxFraction?: number;
  minFontScale?: number;
}

export interface BandLayout {
  /** Итоговый кегль в render-space px. */
  fontSize: number;
  lines: string[];
  /** Итоговая высота плашки в render-space px. */
  bandHeight: number;
  /** true — кегль пришлось уменьшить: текст упёрся в потолок. */
  shrunk: boolean;
}

export interface FitTextBlockInput {
  text: string;
  /** Желаемый кегль в render-space px. */
  fontSize: number;
  lineSpacing: number;
  maxWidth: number;
  /** Сколько высоты доступно блоку, render-space px. */
  maxHeight: number;
  fontFamily: string;
  ctx: CanvasRenderingContext2D;
  minFontScale?: number;
}

export interface FittedTextBlock {
  fontSize: number;
  lines: string[];
  /** Высота блока строк (без внутренних отступов), render-space px. */
  height: number;
  /** true — кегль пришлось уменьшить. */
  shrunk: boolean;
  /** Столько первых строк покрашены целиком первым цветом. */
  primaryLines: number;
  /** Сколько символов строки primaryLines ещё покрашены первым цветом. */
  splitChars: number;
}

/**
 * Общее ядро: уместить блок строк в доступную высоту, не обрезая текст.
 *
 * Заданный кегль уважается, пока блок помещается. Не помещается — кегль
 * ужимается, но не ниже пола. Если и на полу не влезает, возвращаем результат
 * на полу как есть: резать текст нельзя, пусть вызывающий решает, куда его деть.
 */
export function fitTextBlock(input: FitTextBlockInput): FittedTextBlock {
  const spacing = input.lineSpacing > 0 ? input.lineSpacing : 1.2;
  const layoutAt = (size: number) => {
    const { lines, primaryLines, splitChars } = wrapTextWithBreaks(input.text, input.maxWidth, size, input.fontFamily, input.ctx);
    return { lines, primaryLines, splitChars, height: lines.length * size * spacing };
  };

  const requested = layoutAt(input.fontSize);
  if (requested.height <= input.maxHeight) {
    return { fontSize: input.fontSize, lines: requested.lines, height: requested.height, shrunk: false, primaryLines: requested.primaryLines, splitChars: requested.splitChars };
  }

  const floor = Math.max(1, input.fontSize * (input.minFontScale ?? BAND_DEFAULTS.minFontScale));
  const atFloor = layoutAt(floor);
  if (atFloor.height > input.maxHeight) {
    return { fontSize: floor, lines: atFloor.lines, height: atFloor.height, shrunk: true, primaryLines: atFloor.primaryLines, splitChars: atFloor.splitChars };
  }

  let best = { size: floor, ...atFloor };
  let lo = floor;
  let hi = input.fontSize;
  for (let i = 0; i < 12 && hi - lo > 0.5; i++) {
    const mid = (lo + hi) / 2;
    const at = layoutAt(mid);
    if (at.height <= input.maxHeight) {
      best = { size: mid, ...at };
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return { fontSize: best.size, lines: best.lines, height: best.height, shrunk: true, primaryLines: best.primaryLines, splitChars: best.splitChars };
}

export function resolveBandLayout(input: BandLayoutInput): BandLayout {
  const minHeight = input.minHeight ?? BAND_DEFAULTS.minHeight;
  const maxHeight = Math.max(
    minHeight,
    Math.round(input.canvasHeight * (input.maxFraction ?? BAND_DEFAULTS.maxFraction)),
  );
  const fitted = fitTextBlock({
    text: input.text,
    fontSize: input.fontSize,
    lineSpacing: input.lineSpacing,
    maxWidth: input.maxWidth,
    // Под текст отдана не вся плашка — часть съедают внутренние отступы.
    maxHeight: maxHeight * BAND_DEFAULTS.textFraction,
    fontFamily: input.fontFamily,
    ctx: input.ctx,
    minFontScale: input.minFontScale,
  });

  const needed = fitted.height / BAND_DEFAULTS.textFraction;
  // Даже на полу кегля не влезло — плашка перерастает потолок: текст не режем.
  const bandHeight =
    needed > maxHeight
      ? Math.ceil(needed)
      : Math.min(maxHeight, Math.max(minHeight, Math.ceil(needed)));

  return { fontSize: fitted.fontSize, lines: fitted.lines, bandHeight, shrunk: fitted.shrunk };
}

/**
 * Коридор авто-подбора кегля, render-space px @1080 — то есть абсолютные пиксели
 * готовой картинки, а не превью. Это принципиально: `fontSize` задан в
 * preview-space и домножается на scale = 1080 / ширина_превью, поэтому одно и то
 * же число даёт разный результат при разной ширине окна. Коридор в render-space
 * от окна не зависит — авто-режим выдаёт одинаковый кегль на любом экране.
 *
 * Нижняя граница — «минимальная норма для шрифта»: ниже неё не опускаемся даже
 * если текст не влезает (тогда он вылезет, но останется читаемым). Верхняя не
 * даёт короткому заголовку раздуться во весь кадр, чтобы посты между собой не
 * скакали по размеру.
 */
export const AUTO_FONT_RANGE = { min: 40, max: 72 } as const;

/*
 * Откуда числа. Проверено в браузере на настоящих шрифтах всех 42 шаблонов
 * (Bebas, Impact, Calibri, Inter, SamsungSharpSans…) на заголовках в 21, 44 и
 * 78 символов.
 *
 * Верх 72 — на нём сходятся короткие заголовки у 38 шаблонов из 42: именно он
 * и делает посты похожими друг на друга, чего и просила медиа. Выше поднимать
 * нельзя — начнёт разъезжаться.
 *
 * Низ 40 — «минимальная норма». Вступает в силу только у пабликов с самыми
 * тесными плашками и только на самых длинных заголовках; у остальных потолок
 * плашки выше низа коридора, и низ на них не влияет. Опущен с 44 после
 * измерения настоящих плашек: у steppe.daily под текст 169 px, и при 44 длинный
 * заголовок выходил за оранжевую полосу.
 */


/** Заведомо недостижимый верх для поиска потолка плашки, render-space px. */
const CEILING_PROBE = 400;

export interface AutoFitInput {
  text: string;
  lineSpacing: number;
  maxWidth: number;
  /** Сколько высоты доступно блоку, render-space px. */
  maxHeight: number;
  fontFamily: string;
  ctx: CanvasRenderingContext2D;
  /** Границы коридора в render-space px. По умолчанию AUTO_FONT_RANGE. */
  minFontSize?: number;
  maxFontSize?: number;
  /**
   * Высота, по которой считается `ceiling`. По умолчанию равна maxHeight.
   * Задаётся отдельно, когда авто-подбор целится в область с отступами, а
   * ручной предел — во всю плашку: иначе авто сразу садится на потолок и
   * кнопка A+ оказывается мёртвой с первого рендера.
   */
  ceilingMaxHeight?: number;
}

export interface AutoFittedText extends FittedTextBlock {
  /** Самый крупный кегль, при котором блок ещё влезает, render-space px. */
  ceiling: number;
}

/**
 * Подобрать кегль под заголовок автоматически: берём максимум, который влезает
 * в плашку, и зажимаем в коридор. Редактору не нужно знать ни шрифт, ни размер —
 * у каждого паблика свой запас по высоте, и число тут всё равно зависит не от
 * паблика, а от паблика × конкретного заголовка.
 *
 * Отдаём и `ceiling` — он нужен интерфейсу, чтобы кнопка A+ упиралась в потолок
 * плашки, а не позволяла выставить заведомо невлезающий размер.
 */
export function autoFitFontSize(input: AutoFitInput): AutoFittedText {
  const common = {
    text: input.text,
    lineSpacing: input.lineSpacing,
    maxWidth: input.maxWidth,
    maxHeight: input.maxHeight,
    fontFamily: input.fontFamily,
    ctx: input.ctx,
  };
  // Пола нет — ищем именно потолок, каким бы мелким он ни оказался.
  const probeAt = (h: number) => fitTextBlock({
    ...common, maxHeight: h, fontSize: CEILING_PROBE, minFontScale: 1 / CEILING_PROBE,
  }).fontSize;

  // Выбираем размер по своей области (с полями внутри плашки), а сообщаем
  // наверх потолок по всей плашке. Мерить обе одним числом нельзя: тогда
  // авто садилось бы ровно на предел, и кнопка A+ была бы мертва сразу.
  const fitCeiling = probeAt(input.maxHeight);
  const ceiling = input.ceilingMaxHeight === undefined
    ? fitCeiling
    : probeAt(input.ceilingMaxHeight);

  const max = Math.max(1, input.maxFontSize ?? AUTO_FONT_RANGE.max);
  const min = Math.min(Math.max(1, input.minFontSize ?? AUTO_FONT_RANGE.min), max);
  const chosen = Math.min(max, Math.max(min, fitCeiling));

  // Кегль уже выбран оптимально — ужимать его дальше нечего (minFontScale: 1).
  // Если даже минимум не влез, текст вылезет: норму по размеру не нарушаем.
  const fitted = fitTextBlock({ ...common, fontSize: chosen, minFontScale: 1 });
  return { ...fitted, ceiling };
}
